/* eslint-disable no-param-reassign */
import { navigateTo, useRoute } from '#imports'
import { type Ref, computed, watch } from 'vue'

import type { Facet } from '@backmarket/nuxt-layer-recommendation/filtering/types'

import {
  getOpenFilterHashRedirect,
  getThinResultsRedirect,
  getUnknownQueryKeyRedirect,
} from '../utils/filterRedirects'
import { fullyDecodeUriComponent } from '../utils/fullyDecodeUriComponent'

const POSITION_PREFIX_RE = /^\d+\s+(.+)$/
const QUERY_FILTER_TITLE_SEPARATOR = ' • '

export function useOpenFilter(
  openFacets: Ref<string[]>,
  openFacetsError: Ref<unknown>,
  pageData?: {
    pageTitle: string
    configBreadcrumb: { label: string; url: string }[]
    facets: Ref<Facet[]>
  },
) {
  const route = useRoute()

  const openFilter = computed(() => {
    const key = openFacets.value.find((facet) => route.query[facet] != null)
    if (!key) return null
    const raw = route.query[key]
    const value = Array.isArray(raw) ? raw[0] : raw
    if (!value) return null

    const facetLabel = pageData?.facets.value
      .find((f) => f.name === key)
      ?.values.find((v) => v.name === value)?.label

    const label =
      facetLabel ??
      fullyDecodeUriComponent(value).replace(POSITION_PREFIX_RE, '$1')

    return { key, value, label }
  })

  function routeContext() {
    return { path: route.path, query: route.query, hash: route.hash }
  }

  async function redirectInvalidFilters() {
    const unknownQueryKeyRedirect = getUnknownQueryKeyRedirect(
      routeContext(),
      openFilter.value?.key ?? null,
      Boolean(openFacetsError.value),
    )
    if (unknownQueryKeyRedirect) {
      await navigateTo(unknownQueryKeyRedirect, { redirectCode: 301 })

      return
    }

    const openFilterHashRedirect = getOpenFilterHashRedirect(
      routeContext(),
      openFilter.value?.key ?? null,
    )
    if (openFilterHashRedirect) {
      await navigateTo(openFilterHashRedirect, { redirectCode: 301 })
    }
  }

  async function redirectThinResults(total: number) {
    const thinResultsRedirect = getThinResultsRedirect(
      routeContext(),
      openFilter.value?.key ?? null,
      total,
    )
    if (thinResultsRedirect) {
      await navigateTo(thinResultsRedirect, { redirectCode: 302 })
    }
  }

  function watchOpenFilter(filtersInput: Record<string, string[]>) {
    watch(
      openFilter,
      (current, previous) => {
        if (
          current &&
          (current.key !== previous?.key || current.value !== previous?.value)
        ) {
          for (const key of Object.keys(filtersInput)) {
            if (key !== current.key) {
              filtersInput[key] = []
            }
          }
        }

        if (previous?.key && previous.key !== current?.key) {
          const prevValues = filtersInput[previous.key]
          if (prevValues?.length === 1 && prevValues[0] === previous.value) {
            filtersInput[previous.key] = []
          }
        }

        if (current) {
          filtersInput[current.key] = [current.value]
        }
      },
      { flush: 'sync' },
    )
  }

  const title = computed(() => {
    if (!pageData || !openFilter.value) return pageData?.pageTitle ?? ''

    return `${pageData.pageTitle}${QUERY_FILTER_TITLE_SEPARATOR}${openFilter.value.label}`
  })

  const breadcrumb = computed(() => {
    if (!pageData || !openFilter.value) return pageData?.configBreadcrumb ?? []

    return [
      ...pageData.configBreadcrumb,
      {
        label: openFilter.value.label,
        url: `${route.path}?${openFilter.value.key}=${encodeURIComponent(openFilter.value.value)}`,
      },
    ]
  })

  return {
    openFilter,
    redirectInvalidFilters,
    redirectThinResults,
    watchOpenFilter,
    title,
    breadcrumb,
  }
}
